import { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, CircularProgress, Box } from '@mui/material';
import { SystemUpdate, NewReleases } from '@mui/icons-material';

const UpdatePromptPopup = ({ open, onClose }) => {
  const [isUpdating, setIsUpdating] = useState(false);

  const handleUpdateClick = async () => {
    setIsUpdating(true);
    console.log('UpdatePromptPopup: Update clicked, clearing caches and activating new service worker');

    try {
      // Send messages to service worker
      if (navigator.serviceWorker) {
        const registration = await navigator.serviceWorker.ready;
        if (registration.active) {
          // Clear caches first
          registration.active.postMessage({ type: 'CLEAR_CACHE' });
          
          // Then activate new service worker
          setTimeout(() => {
            registration.active.postMessage({ type: 'SKIP_WAITING' });
          }, 500);
        }
      }

      // Clear localStorage (except essential items)
      const essentialKeys = ['installPromptShown', 'installPromptDismissed'];
      const keysToPreserve = {};
      
      essentialKeys.forEach(key => {
        const value = localStorage.getItem(key);
        if (value) {
          keysToPreserve[key] = value;
        }
      });
      
      localStorage.clear();
      
      // Restore essential keys
      Object.entries(keysToPreserve).forEach(([key, value]) => {
        localStorage.setItem(key, value);
      });
      
      console.log('UpdatePromptPopup: localStorage cleared and essential keys restored');

      // Activate waiting service worker if available
      if (navigator.serviceWorker) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (let registration of registrations) {
          if (registration.waiting) {
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          }
        }
      }

      // Initialize Clarity after update
      setTimeout(() => {
        initializeClarity();
      }, 500);

      // Reload after ensuring service worker activation
      setTimeout(() => {
        console.log('UpdatePromptPopup: Reloading page to apply update');
        window.location.reload(true); // Force reload from server
      }, 1500);

    } catch (error) {
      console.error('UpdatePromptPopup: Error during update:', error);
      // Fallback - force reload
      setTimeout(() => {
        window.location.reload(true);
      }, 1000);
    }
  };

  const handleNotNow = () => {
    console.log('UpdatePromptPopup: User dismissed update prompt');
    localStorage.setItem('updatePromptShown', 'true');
    onClose();
  };

  // Initialize Clarity function
  const initializeClarity = () => {
    console.log('UpdatePromptPopup: Re-initializing Clarity after update');
    (function(c,l,a,r,i,t,y){
      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "scnbpo5jyr");
  };

  return (
    <Dialog
      open={open}
      onClose={isUpdating ? null : handleNotNow}
      PaperProps={{
        sx: {
          width: { xs: '90%', sm: 400 },
          maxWidth: '100%',
          margin: 'auto',
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        {isUpdating ? (
          <>
            <SystemUpdate sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="h6">Updating App</Typography>
          </>
        ) : (
          <>
            <NewReleases sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="h6">New Version Available</Typography>
          </>
        )}
      </DialogTitle>
      <DialogContent>
        {isUpdating ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={40} />
            <Typography variant="body1" sx={{ textAlign: 'center' }}>
              Updating to the latest version...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              Please wait while we update your app with the latest features and improvements.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              A new version of the app is available with exciting new features and improvements!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Update now to get the best experience.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 3 }}>
        {!isUpdating && (
          <>
            <Button onClick={handleNotNow} color="text">
              Not Now
            </Button>
            <Button 
              onClick={handleUpdateClick} 
              variant="contained" 
              color="primary"
              sx={{ minWidth: 120 }}
            >
              Update Now
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UpdatePromptPopup;